"use client"
import { createElement, type ReactNode, type Ref } from "react"
import { useEditRegion } from "@/lib/edit/edit-region-registry"

/** Tags `EditableField` can render as. Extend when a call site needs another. */
type EditableTag = "div" | "span" | "p" | "h1" | "h2" | "h3" | "h4" | "section"

/**
 * Client leaf that registers an inline-editor region around server-rendered
 * content, so a Server Component page can expose a CMS field as editable
 * without itself becoming a client component. The RSC page renders the field's
 * content and passes it in as `children`; this leaf attaches the live element
 * to the edit registry on mount. Pass `collection`/`id`/`field`/`locale`/`kind`
 * as string-literal props at the call site so the stamp-coverage lint can see
 * them statically. (AHD-7396)
 */
export function EditableField({
  collection,
  id,
  field,
  locale,
  kind,
  as = "div",
  className,
  children,
}: {
  collection: string
  id?: string
  field: string
  locale: string
  kind: "scalar" | "html" | "lexical"
  as?: EditableTag
  className?: string
  children: ReactNode
}) {
  const ref = useEditRegion<HTMLElement>(
    id ? { collection, id, field, locale, kind } : null
  )
  return createElement(
    as,
    { ref: ref as Ref<HTMLElement>, className },
    children
  )
}
